import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

const FLAG_ICONS = {
  done: 'ri-checkbox-circle-fill',
  counter: 'ri-arrow-left-right-line',
};

const FLAG_LABELS = {
  done: 'Responded',
  counter: 'Counter proposed',
};

const scrollToSection = id => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Sticky "on this page" bar for the Input Offering tab: response counts on
 * top and one jump link per section below (wrapping, no horizontal scroll),
 * highlighting the section in view. Horizontal on purpose so the delivery
 * tables keep the full content width.
 */
const OfferSectionNav = ({ sections, counterCount, noteCount, isReadOnly }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const sectionKey = sections.map(section => section.id).join('|');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const elements = sectionKey
      .split('|')
      .map(id => document.getElementById(id))
      .filter(Boolean);
    if (elements.length === 0) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        const inView = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView[0]) setActiveId(inView[0].target.id);
      },
      { rootMargin: '-140px 0px -55% 0px' }
    );
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionKey]);

  return (
    <nav className='od-nav d-print-none' aria-label='Offer sections'>
      <div className='od-nav__summary'>
        <span
          className='od-stat od-stat--counter'
          title='Term counters proposed'
        >
          <span className='od-stat__value'>{counterCount}</span>
          <span className='od-stat__label'>Counters</span>
        </span>
        <span className='od-stat od-stat--note' title='Response notes sent'>
          <span className='od-stat__value'>{noteCount}</span>
          <span className='od-stat__label'>Notes</span>
        </span>
        <span
          className={classnames('od-nav__state', {
            'od-nav__state--locked': isReadOnly,
          })}
          title={
            isReadOnly
              ? 'This offer can no longer be edited'
              : 'Save a draft any time before submitting'
          }
        >
          <i
            className={isReadOnly ? 'ri-lock-line' : 'ri-edit-2-line'}
            aria-hidden='true'
          />
          {isReadOnly ? 'View only' : 'Editable'}
        </span>
      </div>

      <ul className='od-nav__list'>
        {sections.map(section => (
          <li key={section.id}>
            <button
              type='button'
              className={classnames('od-nav__item', {
                'is-active': activeId === section.id,
              })}
              aria-current={activeId === section.id ? 'true' : undefined}
              onClick={() => {
                setActiveId(section.id);
                scrollToSection(section.id);
              }}
            >
              <span className='od-nav__icon' aria-hidden='true'>
                <i className={section.icon} />
              </span>
              <span className='od-nav__label'>{section.label}</span>
              {section.meta != null && (
                <span className='od-nav__meta'>{section.meta}</span>
              )}
              {section.flag && (
                <i
                  className={`od-nav__flag od-nav__flag--${section.flag} ${FLAG_ICONS[section.flag]}`}
                  title={FLAG_LABELS[section.flag]}
                  aria-label={FLAG_LABELS[section.flag]}
                />
              )}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

OfferSectionNav.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      meta: PropTypes.node,
      flag: PropTypes.oneOf(['done', 'counter']),
    })
  ).isRequired,
  counterCount: PropTypes.number,
  noteCount: PropTypes.number,
  isReadOnly: PropTypes.bool,
};

export default OfferSectionNav;
